import { MantineProvider } from '@mantine/core';
import _sum from 'lodash/sum';
import type { DataTableColumn } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import { useMemo } from 'react';
import { type Control, useWatch } from 'react-hook-form';

import type {
  EstimationItemCostResponse,
  EstimationModelResponse,
} from '@/api/schema/data-contracts';
import { FormattedNumber } from '@/components/atoms/FormattedNumber';
import { SanitizedHtml } from '@/components/atoms/SanitizedHtml';
import { TextFormField } from '@/components/atoms/TextFormField';
import { WysiwygFormTextField } from '@/components/atoms/WysiwygFormTextField';
import { entityFieldSettings } from '@/constants/entityFieldSettings';
import { useMessages } from '@/hooks/messages';
import { sortCostTypes } from '@/utils/sorting';

import type { CostEstimationItemDetail, CostEstimationItemsEditFormModel } from '../../model'; // TODO: either move it to common place or create a copy specifically for this component
import { directCostsMessageDefinitions } from './messages'; // TODO: either move it to common place or create a copy specifically for this component

interface EstimationItemEditationProps {
  control: Control<CostEstimationItemsEditFormModel>;
  estimationModel: EstimationModelResponse;
  estimationItem: CostEstimationItemDetail;
  isReadOnly: boolean;
}

interface Row {
  costs: EstimationItemCostResponse[];
  id: '';
}

export const MainEstimationItemDetail: FC<EstimationItemEditationProps> = ({
  control,
  estimationModel,
  estimationItem,
  isReadOnly,
}) => {
  const messages = useMessages(directCostsMessageDefinitions);
  const formModel = useWatch({ control });

  if (!formModel.mainEstimationItem) {
    throw new Error('mainEstimationItem cannot be undefined');
  }

  const allCostTypes = useMemo(
    () => sortCostTypes(estimationModel.costTypes, estimationModel.costTypeGroups),
    [estimationModel.costTypes, estimationModel.costTypeGroups],
  );

  const columns: DataTableColumn<Row>[] = [
    ...allCostTypes.map<DataTableColumn<Row>>((costType) => ({
      accessor: `cost_type_id_${costType.id}`,
      title: costType.name,
      noWrap: true,
      textAlign: 'right',
      render: (row) => (
        <FormattedNumber value={row.costs?.find((x) => x.costTypeId === costType.id)?.price} />
      ),
    })),
    {
      accessor: 'sum',
      title: messages.detailSum(),
      noWrap: true,
      textAlign: 'right',
      render: (row) => (
        <FormattedNumber value={_sum(row.costs.map((itemCost) => itemCost.price ?? 0))} />
      ),
    },
  ];

  return (
    <div className='space-y-5'>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <div>
          <label htmlFor='mainEstimationItem.name'>{messages.estimationItemName()}</label>
          {isReadOnly ? (
            formModel.mainEstimationItem.name
          ) : (
            <TextFormField
              control={control}
              fieldPath='mainEstimationItem.name'
              rules={{
                required: true,
                maxLength: entityFieldSettings.estimationItemEntity.nameMaxLength,
              }}
            />
          )}
        </div>
      </div>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <div>
          <label htmlFor='mainEstimationItem.description'>{messages.description()}</label>
          {isReadOnly ? (
            <SanitizedHtml htmlString={formModel.mainEstimationItem.description} />
          ) : (
            <WysiwygFormTextField
              control={control}
              fieldPath='mainEstimationItem.description'
              rules={{ maxLength: entityFieldSettings.estimationItemEntity.descriptionMaxLength }}
            />
          )}
        </div>
      </div>
      <MantineProvider>
        <div className='relative'>
          <div className='pointer-events-none absolute top-0 right-0 z-[5] block h-full w-8 bg-gradient-to-l from-white/80 to-transparent dark:from-black/80' />
          <DataTable<Row>
            highlightOnHover
            withTableBorder
            className='table-hover rounded-xl p-2 whitespace-nowrap'
            records={[
              {
                costs: estimationItem.estimationItemsCosts ?? [],
                id: '' /* id is needed just to prevent react warning about missing key property */,
              },
            ]}
            columns={columns}
          />
        </div>
      </MantineProvider>
    </div>
  );
};
